// // src/components/page/article/ArticleList.tsx
// 'use client';

// import { useState, useCallback } from 'react';
// import ArticleCard from './ArticleCard';
// import type { Article } from '@/model/article';
// import { ArticleService } from '@/libs/services/article';
// import InfiniteListWithFetch from '@/components/ui/list/InfiniteListWithFetch';
// import Skeleton from '@/components/ui/skeleton/Skeleton';
// import { EmptyState, EMPTY, buildEmptyState } from '@/components/ui/emptyState';

// type FilterKind = 'kol' | 'store';

// type Props = {
//   /** 進入貼文前的鉤子（例如關閉 modal、打點） */
//   onBeforeEnterPost?: () => void;
//   /**
//    * 之後如果要區分「互動貼文 / 追蹤動態」，可以用 kind
//    * 目前後端 API 尚未分開，所以暫時沒用到，只先保留型別
//    */
//   kind?: 'article' | 'article_follow';

//   /**
//    * ✅ 篩選條件：
//    * - 'kol'   → 用 kol_id = filterId
//    * - 'store' → 同時用 store_id = filterId + ar_store_id = filterId
//    */
//   filterKind?: FilterKind;
//   filterId?: number | string;
// };

// const PAGE_SIZE = 10;

// /** 一張文章卡片的 Skeleton */
// function renderArticleSkeleton(index: number) {
//   return (
//     <div key={`article-sk-${index}`} className="rounded-4 shadow-sm p-5 mb-5">
//       <div>
//         {/* avatar + name */}
//         <div className="d-flex align-items-center mb-3">
//           <Skeleton width="42px" height="42px" radius="50%" />
//           <Skeleton width="120px" height="14px" radius="999px" className="ms-2" />
//         </div>

//         {/* title */}
//         <Skeleton width="80%" height="18px" radius="4px" className="mb-2" />

//         {/* content 兩行 */}
//         <Skeleton width="100%" height="12px" radius="4px" className="mb-1" />
//         <Skeleton width="90%" height="12px" radius="4px" className="mb-3" />

//         {/* bottom stats */}
//         <div className="d-flex align-items-center gap-3 mt-3">
//           <Skeleton width="90px" height="12px" radius="4px" />
//           <Skeleton width="90px" height="12px" radius="4px" />
//         </div>
//       </div>
//     </div>
//   );
// }

// export default function ArticleList({
//   onBeforeEnterPost,
//   kind = 'article',
//   filterKind,
//   filterId,
// }: Props) {
//   // 用來記錄「已刪除的文章 id」，只在前端隱藏，不刷新整頁
//   const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

//   const handleDeleted = useCallback((deletedId: string) => {
//     setHiddenIds((prev) => {
//       if (prev.has(deletedId)) return prev;
//       const next = new Set(prev);
//       next.add(deletedId);
//       return next;
//     });
//   }, []);

//   // fetchData 給 InfiniteListWithFetch 使用
//   const fetchData = useCallback(
//     async ({ page, pageSize }: { page: number; pageSize?: number }) => {
//       if (kind === 'article_follow') {
//         // TODO: 未來改成呼叫「追蹤動態」專用 API
//         // 目前先沿用一般文章列表邏輯
//       }

//       const size = pageSize ?? PAGE_SIZE;

//       /**
//        * 特別處理：店家頁（同時要抓 store 一般貼文 + AR 貼文）
//        * - 一次打兩支 /article：
//        *   1) store_id = filterId
//        *   2) is_ar = 1, ar_store_id = filterId
//        * - 再在前端 merge + 去重 + double-check
//        */
//       if (filterKind === 'store' && filterId != null) {
//         const target = String(filterId);

//         // 1) 店家一般貼文
//         const storeParams: Parameters<typeof ArticleService.list>[0] = {
//           page,
//           store_id: filterId,
//         };

//         // 2) 這家店的 AR 貼文
//         const arParams: Parameters<typeof ArticleService.list>[0] = {
//           page,
//           ar_store_id: filterId,
//           is_ar: 1,
//         };

//         const [storeList, arList] = await Promise.all([
//           ArticleService.list(storeParams),
//           ArticleService.list(arParams),
//         ]);

//         // 合併 + 依 id 去重
//         const mergedMap = new Map<string, Article>();
//         storeList.forEach((a) => mergedMap.set(a.id, a));
//         arList.forEach((a) => mergedMap.set(a.id, a));
//         const merged = Array.from(mergedMap.values());

//         // 再用 storeId / ar_store_id 做一次前端 double-check
//         const filtered = merged.filter((item) => {
//           const storeIdStr =
//             item.storeId !== undefined && item.storeId !== null ? String(item.storeId) : '';

//           const arStoreIdStr =
//             item.ar?.ar_store_id !== undefined && item.ar?.ar_store_id !== null
//               ? String(item.ar.ar_store_id)
//               : '';

//           const match = storeIdStr === target || arStoreIdStr === target;

//           // 如果你想看到底有沒有 match，可以暫時打開這個 log：
//           if (match) {
//             console.log('[ArticleList][store merged match]', {
//               articleId: item.id,
//               storeIdStr,
//               arStoreIdStr,
//               target,
//             });
//           }

//           return match;
//         });

//         // hasMore：任一邊還有資料就算還有更多
//         const hasMore = storeList.length >= size || arList.length >= size;

//         return {
//           items: filtered,
//           hasMore,
//         };
//       }

//       /**
//        * 其他情況（含 filterKind === 'kol' 或沒有 filter）
//        * - 只打一支 ArticleService.list
//        */
//       const params: Parameters<typeof ArticleService.list>[0] = {
//         page,
//       };

//       // kol 專用 query
//       if (filterKind === 'kol' && filterId != null) {
//         params.kol_id = filterId;
//       }

//       const list = await ArticleService.list(params);

//       // 再在前端用 normalize 過的欄位 double-check
//       let filtered: Article[] = list;

//       // kol：用 author.id double-check，避免後端資料有髒值
//       if (filterKind === 'kol' && filterId != null) {
//         const target = String(filterId);
//         filtered = list.filter((item) => item.author?.id === target);
//       }

//       // hasMore 還是用「原始 list 長度」算，避免過早停住
//       const hasMore = list.length >= size;

//       return {
//         items: filtered,
//         hasMore,
//       };
//     },
//     [kind, filterKind, filterId]
//   );

//   return (
//     <section>
//       <InfiniteListWithFetch<Article>
//         fetchData={fetchData}
//         pageSize={PAGE_SIZE}
//         className=""
//         getKey={(item, idx) => `${item.id}-${idx}`}
//         renderItem={(item) =>
//           // 若該文章已被刪除，就不再渲染卡片（不刷新整頁）
//           hiddenIds.has(item.id) ? null : (
//             <ArticleCard
//               article={item}
//               onBeforeEnterPost={onBeforeEnterPost}
//               onDeleted={handleDeleted}
//             />
//           )
//         }
//         renderSkeleton={renderArticleSkeleton}
//         skeletonMode="matchLoadedOrPrev"
//         skeletonInitialCount={5}
//         rootMargin="200px"
//         emptyView={<EmptyState {...buildEmptyState(EMPTY.article.noPosts)} />}
//         errorView={<div className="text-center py-5 text-danger">文章載入失敗，請稍後再試</div>}
//       />
//     </section>
//   );
// }

// // src/components/page/article/ArticleList.tsx
// 'use client';

// import { useCallback, useEffect, useMemo, useState } from 'react';
// import ArticleCard from './ArticleCard';
// import type { Article } from '@/model/article';
// import { ArticleService } from '@/libs/services/article';
// import InfiniteListWithFetch from '@/components/ui/list/InfiniteListWithFetch';
// import Skeleton from '@/components/ui/skeleton/Skeleton';
// import { EmptyState, EMPTY, buildEmptyState } from '@/components/ui/emptyState';
// import type { FetchParams } from '@/hooks/useInfiniteList';

// type FilterKind = 'kol' | 'store';

// type Props = {
//   /** 進入貼文前的鉤子（例如關閉 modal、打點） */
//   onBeforeEnterPost?: () => void;

//   kind?: 'article' | 'article_follow';

//   /**
//    * ✅ 篩選條件：
//    * - 'kol'   → 用 kol_id = filterId
//    * - 'store' → 同時用 store_id = filterId + ar_store_id = filterId
//    */
//   filterKind?: FilterKind;
//   filterId?: number | string;
// };

// const PAGE_SIZE = 10;

// /** 一張文章卡片的 Skeleton */
// function renderArticleSkeleton(index: number) {
//   return (
//     <div key={`article-sk-${index}`} className="rounded-4 shadow-sm p-5 mb-5">
//       <div>
//         {/* avatar + name */}
//         <div className="d-flex align-items-center mb-3">
//           <Skeleton width="42px" height="42px" radius="50%" />
//           <Skeleton width="120px" height="14px" radius="999px" className="ms-2" />
//         </div>

//         {/* title */}
//         <Skeleton width="80%" height="18px" radius="4px" className="mb-2" />

//         {/* content 兩行 */}
//         <Skeleton width="100%" height="12px" radius="4px" className="mb-1" />
//         <Skeleton width="90%" height="12px" radius="4px" className="mb-3" />

//         {/* bottom stats */}
//         <div className="d-flex align-items-center gap-3 mt-3">
//           <Skeleton width="90px" height="12px" radius="4px" />
//           <Skeleton width="90px" height="12px" radius="4px" />
//         </div>
//       </div>
//     </div>
//   );
// }

// export default function ArticleList({
//   onBeforeEnterPost,
//   kind = 'article',
//   filterKind,
//   filterId,
// }: Props) {
//   // 用來記錄「已刪除的文章 id」，只在前端隱藏，不刷新整頁
//   const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

//   const handleDeleted = useCallback((deletedId: string) => {
//     setHiddenIds((prev) => {
//       if (prev.has(deletedId)) return prev;
//       const next = new Set(prev);
//       next.add(deletedId);
//       return next;
//     });
//   }, []);

//   /**
//    * ✅ resetKey：篩選/來源變更就 reset list
//    * - 否則同一個 component 還在時，page 會繼續累加，造成混資料/重複撈的錯覺
//    */
//   const resetKey = useMemo(
//     () => `${kind ?? 'article'}|${filterKind ?? 'none'}|${filterId ?? 'none'}`,
//     [kind, filterKind, filterId]
//   );

//   // ✅ 切換列表時，也把「已刪除隱藏」清掉，避免跨篩選誤傷
//   useEffect(() => {
//     setHiddenIds(new Set());
//   }, [resetKey]);

//   // fetchData 給 InfiniteListWithFetch 使用
//   const fetchData = useCallback(
//     async ({ page, pageSize, signal }: FetchParams) => {
//       if (signal?.aborted) {
//         // 讓上層 hook 正確視為 aborted（不顯示 error）
//         throw new DOMException('Aborted', 'AbortError');
//       }

//       if (kind === 'article_follow') {
//         // TODO: 未來改成呼叫「追蹤動態」專用 API
//         // 目前先沿用一般文章列表邏輯
//       }

//       const size = pageSize ?? PAGE_SIZE;

//       /**
//        * 特別處理：店家頁（同時要抓 store 一般貼文 + AR 貼文）
//        */
//       if (filterKind === 'store' && filterId != null) {
//         const target = String(filterId);

//         const storeParams: Parameters<typeof ArticleService.list>[0] = {
//           page,
//           store_id: filterId,
//         };

//         const arParams: Parameters<typeof ArticleService.list>[0] = {
//           page,
//           ar_store_id: filterId,
//           is_ar: 1,
//         };

//         const [storeList, arList] = await Promise.all([
//           ArticleService.list(storeParams),
//           ArticleService.list(arParams),
//         ]);

//         if (signal?.aborted) {
//           throw new DOMException('Aborted', 'AbortError');
//         }

//         // 合併 + 依 id 去重
//         const mergedMap = new Map<string, Article>();
//         storeList.forEach((a) => mergedMap.set(a.id, a));
//         arList.forEach((a) => mergedMap.set(a.id, a));
//         const merged = Array.from(mergedMap.values());

//         // 再用 storeId / ar_store_id 做一次前端 double-check
//         const filtered = merged.filter((item) => {
//           const storeIdStr =
//             item.storeId !== undefined && item.storeId !== null ? String(item.storeId) : '';

//           const arStoreIdStr =
//             item.ar?.ar_store_id !== undefined && item.ar?.ar_store_id !== null
//               ? String(item.ar.ar_store_id)
//               : '';

//           return storeIdStr === target || arStoreIdStr === target;
//         });

//         // hasMore：任一邊還有資料就算還有更多（用原始 list 長度判斷避免過早停住）
//         const hasMore = storeList.length >= size || arList.length >= size;

//         return { items: filtered, hasMore };
//       }

//       /**
//        * 其他情況（含 filterKind === 'kol' 或沒有 filter）
//        */
//       const params: Parameters<typeof ArticleService.list>[0] = { page };

//       if (filterKind === 'kol' && filterId != null) {
//         params.kol_id = filterId;
//       }

//       const list = await ArticleService.list(params);

//       if (signal?.aborted) {
//         throw new DOMException('Aborted', 'AbortError');
//       }

//       let filtered: Article[] = list;

//       // kol：用 author.id double-check
//       if (filterKind === 'kol' && filterId != null) {
//         const target = String(filterId);
//         filtered = list.filter((item) => item.author?.id === target);
//       }

//       // hasMore 還是用「原始 list 長度」算，避免過早停住
//       const hasMore = list.length >= size;

//       return { items: filtered, hasMore };
//     },
//     [kind, filterKind, filterId]
//   );

//   return (
//     <section>
//       <InfiniteListWithFetch<Article>
//         fetchData={fetchData}
//         pageSize={PAGE_SIZE}
//         resetKey={resetKey}
//         resetKind="other"
//         className=""
//         // ✅ 穩定 key：避免 `${id}-${idx}` 造成重掛/抖動/看似重複
//         getKey={(item) => item.id}
//         renderItem={(item) =>
//           hiddenIds.has(item.id) ? null : (
//             <ArticleCard
//               article={item}
//               onBeforeEnterPost={onBeforeEnterPost}
//               onDeleted={handleDeleted}
//             />
//           )
//         }
//         renderSkeleton={renderArticleSkeleton}
//         skeletonMode="matchLoadedOrPrev"
//         skeletonInitialCount={5}
//         rootMargin="200px"
//         emptyView={<EmptyState {...buildEmptyState(EMPTY.article.noPosts)} />}
//         errorView={<div className="text-center py-5 text-danger">文章載入失敗，請稍後再試</div>}
//       />
//     </section>
//   );
// }

// src/components/page/article/ArticleList.tsx
'use client';

import { useState, useCallback, useRef } from 'react';
import ArticleCard from './ArticleCard';
import type { Article } from '@/model/article';
import { ArticleService } from '@/libs/services/article';
import InfiniteListWithFetch from '@/components/ui/list/InfiniteListWithFetch';
import Skeleton from '@/components/ui/skeleton/Skeleton';
import { EmptyState, EMPTY, buildEmptyState } from '@/components/ui/emptyState';

type FilterKind = 'kol' | 'store';

type Props = {
  onBeforeEnterPost?: () => void;
  kind?: 'article' | 'article_follow';
  filterKind?: FilterKind;
  filterId?: number | string;
};

const PAGE_SIZE = 10;

/** store 頁只顯示這兩種 table */
const STORE_TABLE_ALLOWLIST = new Set(['collection', 'store_article']);

function normStr(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** 兼容 table 欄位命名 */
function getArticleTable(item: Article): string {
  const anyItem = item as unknown as { table?: unknown; table_name?: unknown; tableName?: unknown };
  const t = anyItem.table ?? anyItem.table_name ?? anyItem.tableName;
  return normStr(t);
}

function isAllowedStoreTable(item: Article) {
  return STORE_TABLE_ALLOWLIST.has(getArticleTable(item));
}

/** ✅ 唯一鍵：你現在 normalize 的 id 本身就像 collection_5367，非常適合當 key（記得 trim） */
function getArticleUniqueKey(item: Article): string {
  const anyItem = item as unknown as { id?: unknown; table_id?: unknown; tableId?: unknown };
  const id = normStr(anyItem.id);
  if (id) return id;

  // fallback（幾乎不會走到）
  const table = getArticleTable(item);
  const tableId = anyItem.tableId ?? anyItem.table_id;
  if (table && (typeof tableId === 'number' || typeof tableId === 'string')) {
    return `${table}_${String(tableId).trim()}`;
  }
  return `${table}_${String(tableId ?? '')}`;
}

function dedupeByUniqueKey(list: Article[]): Article[] {
  const seen = new Set<string>();
  const out: Article[] = [];
  for (const it of list) {
    const k = getArticleUniqueKey(it);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

/** 解析 crtime（容錯） */
function getCrtimeMs(item: Article): number {
  const raw = (item as unknown as { crtime?: unknown; letime?: unknown }).crtime;
  const s = normStr(raw);

  let t = Date.parse(s);
  if (!Number.isNaN(t)) return t;

  if (s.includes(' ') && !s.includes('T')) {
    t = Date.parse(s.replace(' ', 'T'));
    if (!Number.isNaN(t)) return t;
  }

  const le = (item as unknown as { letime?: unknown }).letime;
  const ls = normStr(le);
  t = Date.parse(ls);
  if (!Number.isNaN(t)) return t;

  if (ls.includes(' ') && !ls.includes('T')) {
    t = Date.parse(ls.replace(' ', 'T'));
    if (!Number.isNaN(t)) return t;
  }

  return 0;
}

function compareByCrtimeDesc(a: Article, b: Article): number {
  const ta = getCrtimeMs(a);
  const tb = getCrtimeMs(b);
  if (ta !== tb) return tb - ta;

  const ka = getArticleUniqueKey(a);
  const kb = getArticleUniqueKey(b);
  if (ka === kb) return 0;
  return kb.localeCompare(ka);
}

/**
 * ✅ 放寬版歸屬檢查：
 * - store 流：若 item.storeId 缺欄位，不硬擋（視為後端已用 store_id 篩過）
 * - ar 流：若 item.ar?.ar_store_id 缺欄位，不硬擋（視為後端已用 ar_store_id 篩過）
 */
function belongsToStoreSoft(item: Article, target: string, source: 'store' | 'ar'): boolean {
  if (source === 'store') {
    const v = (item as unknown as { storeId?: unknown }).storeId;
    if (v === undefined || v === null) return true;
    return String(v) === target;
  }
  const arStore = (item as unknown as { ar?: { ar_store_id?: unknown } }).ar?.ar_store_id;
  if (arStore === undefined || arStore === null) return true;
  return String(arStore) === target;
}

/** 一張文章卡片的 Skeleton */
function renderArticleSkeleton(index: number) {
  return (
    <div key={`article-sk-${index}`} className="rounded-4 shadow-sm p-5 mb-5">
      <div>
        <div className="d-flex align-items-center mb-3">
          <Skeleton width="42px" height="42px" radius="50%" />
          <Skeleton width="120px" height="14px" radius="999px" className="ms-2" />
        </div>
        <Skeleton width="80%" height="18px" radius="4px" className="mb-2" />
        <Skeleton width="100%" height="12px" radius="4px" className="mb-1" />
        <Skeleton width="90%" height="12px" radius="4px" className="mb-3" />
        <div className="d-flex align-items-center gap-3 mt-3">
          <Skeleton width="90px" height="12px" radius="4px" />
          <Skeleton width="90px" height="12px" radius="4px" />
        </div>
      </div>
    </div>
  );
}

type PageBase = 0 | 1;

type StoreMergeState = {
  key: string;
  pageBase: PageBase;

  // 後端 page 永遠用 1-based
  storeNextPage: number;
  arNextPage: number;

  storeBuf: Article[];
  arBuf: Article[];
  storeIdx: number;
  arIdx: number;

  storeDone: boolean;
  arDone: boolean;

  /** ✅ 最終輸出序列（永遠不重複） */
  output: Article[];
  outputKeys: Set<string>;
};

function createStoreMergeState(key: string, pageBase: PageBase): StoreMergeState {
  return {
    key,
    pageBase,
    storeNextPage: 1,
    arNextPage: 1,
    storeBuf: [],
    arBuf: [],
    storeIdx: 0,
    arIdx: 0,
    storeDone: false,
    arDone: false,
    output: [],
    outputKeys: new Set(),
  };
}

export default function ArticleList({
  onBeforeEnterPost,
  kind = 'article',
  filterKind,
  filterId,
}: Props) {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const storeMergeRef = useRef<StoreMergeState | null>(null);

  const handleDeleted = useCallback((deletedId: string) => {
    setHiddenIds((prev) => {
      if (prev.has(deletedId)) return prev;
      const next = new Set(prev);
      next.add(deletedId);
      return next;
    });
  }, []);

  const fetchData = useCallback(
    async ({ page, pageSize }: { page: number; pageSize?: number }) => {
      const size = pageSize ?? PAGE_SIZE;

      // ============= store merge =============
      if (filterKind === 'store' && filterId != null) {
        const target = String(filterId);
        const key = `${kind}|${target}`;

        if (!storeMergeRef.current || storeMergeRef.current.key !== key) {
          const base: PageBase = page === 0 ? 0 : 1; // 只在第一次判斷
          storeMergeRef.current = createStoreMergeState(key, base);
        }

        const state = storeMergeRef.current;

        const start = state.pageBase === 0 ? page * size : (page - 1) * size;
        const end = state.pageBase === 0 ? (page + 1) * size : page * size;
        const safeStart = Math.max(0, start);
        const safeEnd = Math.max(0, end);

        const BACKEND_DONE_HINT = size;

        const fetchNextStore = async () => {
          if (state.storeDone) return;

          const list = await ArticleService.list({
            page: state.storeNextPage,
            store_id: filterId,
          });

          state.storeNextPage += 1;
          if (list.length === 0 || list.length < BACKEND_DONE_HINT) state.storeDone = true;

          const eligible = dedupeByUniqueKey(list)
            .filter(isAllowedStoreTable)
            .filter((it) => belongsToStoreSoft(it, target, 'store'))
            .sort(compareByCrtimeDesc);

          state.storeBuf.push(...eligible);
        };

        const fetchNextAr = async () => {
          if (state.arDone) return;

          const list = await ArticleService.list({
            page: state.arNextPage,
            ar_store_id: filterId,
            is_ar: 1,
          });

          state.arNextPage += 1;
          if (list.length === 0 || list.length < BACKEND_DONE_HINT) state.arDone = true;

          const eligible = dedupeByUniqueKey(list)
            .filter(isAllowedStoreTable)
            .filter((it) => belongsToStoreSoft(it, target, 'ar'))
            .sort(compareByCrtimeDesc);

          state.arBuf.push(...eligible);
        };

        // 請求預算（避免一次補太多頁）
        let fetchBudget = safeStart === 0 ? 12 : 6;

        // 先確保有 buffer
        if (state.storeBuf.length === 0 && !state.storeDone && fetchBudget > 0) {
          fetchBudget--;
          await fetchNextStore();
        }
        if (state.arBuf.length === 0 && !state.arDone && fetchBudget > 0) {
          fetchBudget--;
          await fetchNextAr();
        }

        // ✅ 核心：補「output（不重複序列）」到至少 safeEnd
        while (state.output.length < safeEnd) {
          const s = state.storeIdx < state.storeBuf.length ? state.storeBuf[state.storeIdx] : null;
          const a = state.arIdx < state.arBuf.length ? state.arBuf[state.arIdx] : null;

          // 沒候選了 → 嘗試補抓
          if (!s && !a) {
            let didFetch = false;
            if (!state.storeDone && fetchBudget > 0) {
              fetchBudget--;
              didFetch = true;
              await fetchNextStore();
            }
            if (!state.arDone && fetchBudget > 0) {
              fetchBudget--;
              didFetch = true;
              await fetchNextAr();
            }

            const s2 =
              state.storeIdx < state.storeBuf.length ? state.storeBuf[state.storeIdx] : null;
            const a2 = state.arIdx < state.arBuf.length ? state.arBuf[state.arIdx] : null;

            if (!didFetch || (!s2 && !a2)) break;
            continue;
          }

          // 2-way merge：挑較新
          let pick: Article;
          let from: 'store' | 'ar';

          if (s && a) {
            const cmp = compareByCrtimeDesc(s, a);
            if (cmp <= 0) {
              pick = s;
              from = 'store';
            } else {
              pick = a;
              from = 'ar';
            }
          } else if (s) {
            pick = s;
            from = 'store';
          } else {
            pick = a as Article;
            from = 'ar';
          }

          if (from === 'store') state.storeIdx += 1;
          else state.arIdx += 1;

          // ✅ 全域去重（跨頁/跨來源）：一律以 uniqueKey 為準
          const ukey = getArticleUniqueKey(pick);
          if (state.outputKeys.has(ukey)) continue;

          state.outputKeys.add(ukey);
          state.output.push(pick);
        }

        const items = state.output.slice(safeStart, safeEnd);

        // 空頁就直接結束（避免再次觸發造成 UI 抖動）
        if (items.length === 0) return { items: [], hasMore: false };

        const exhausted =
          state.storeDone &&
          state.arDone &&
          state.storeIdx >= state.storeBuf.length &&
          state.arIdx >= state.arBuf.length;

        const hasMore =
          !exhausted &&
          (state.output.length > safeEnd ||
            state.storeIdx < state.storeBuf.length ||
            state.arIdx < state.arBuf.length ||
            !state.storeDone ||
            !state.arDone);

        return { items, hasMore };
      }

      // ============= non-store =============
      const params: Parameters<typeof ArticleService.list>[0] = { page };
      if (filterKind === 'kol' && filterId != null) params.kol_id = filterId;

      const list = await ArticleService.list(params);

      let filtered: Article[] = list;
      if (filterKind === 'kol' && filterId != null) {
        const target = String(filterId);
        filtered = list.filter((item) => item.author?.id === target);
      }

      const hasMore = list.length >= size;
      return { items: filtered, hasMore };
    },
    [kind, filterKind, filterId]
  );

  return (
    <section>
      <InfiniteListWithFetch<Article>
        fetchData={fetchData}
        pageSize={PAGE_SIZE}
        className=""
        getKey={(item) => getArticleUniqueKey(item)}
        renderItem={(item) => {
          const key = getArticleUniqueKey(item);
          const shouldHide = hiddenIds.has(key) || hiddenIds.has((item as any).id);
          return shouldHide ? null : (
            <ArticleCard
              article={item}
              onBeforeEnterPost={onBeforeEnterPost}
              onDeleted={handleDeleted}
            />
          );
        }}
        renderSkeleton={renderArticleSkeleton}
        skeletonMode="matchLoadedOrPrev"
        skeletonInitialCount={5}
        rootMargin="200px"
        emptyView={<EmptyState {...buildEmptyState(EMPTY.article.noPosts)} />}
        errorView={<div className="text-center py-5 text-danger">文章載入失敗，請稍後再試</div>}
      />
    </section>
  );
}
